import { useSnackbar } from 'notistack';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as api from '../api/core/authWindows';
import { AppUser, AppUserWindows } from '../types/core/auth.types';
import { useTranslation } from './translation';
import { useLocation, useNavigate } from 'react-router-dom';
import { paths } from '../utils/core/routes';
import { useSessionStorage } from '../hooks/useSessionStorage';
import { STORAGE_KEYS } from '../utils/core/localStorageKeys';
import { useLocalStorage } from '../hooks/useLocalStorage';

interface AuthContextType {
  user: AppUser | null;
  signIn?: (id: string, password: string, persist: boolean) => void;
  signOut?: () => void;
  isLoading?: boolean;
}

const AuthContext = createContext<AuthContextType>({ user: null });
const useAuth = () => useContext<AuthContextType>(AuthContext);

function AuthProvider(props: any) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAuthSuccess, setAuthSuccess] = useState<boolean>(false);
  const [isSignInSuccess, setSignInSuccess] = useState<boolean>(false);
  const [isSignOutSuccess, setSignOutSuccess] = useState<boolean>(false);
  const [redirectPath, setRedirectPath] = useState<string>('');
  const [storedUserSession, setStoredUserSession] = useSessionStorage(STORAGE_KEYS.USER_KEY, null);
  const [storedUserLocal, setStoredUserLocal] = useLocalStorage(STORAGE_KEYS.USER_KEY, null);
  const [persist, setPersist] = useState<boolean>(false);

  useQuery<boolean>('getIsAuthenticated', api.getIsAuthenticated, {
    onSuccess: (data) => {
      if (data) {
        setAuthSuccess(data);
        return;
      }

      setSignInSuccess(false);
      setSignOutSuccess(true);
      setUser(null);
      sessionStorage.removeItem(STORAGE_KEYS.USER_KEY);
      localStorage.removeItem(STORAGE_KEYS.USER_KEY);
    },
  });

  // useQuery<AppUserWindows>('getCurrentUser', api.getCurrentUser, {
  //   // call only when user is not stored anywhere
  //   enabled: !user || (!storedUserLocal && !storedUserSession),
  //   onSuccess: (data) => {
  //     setUser({
  //       id: data.id,
  //       email: data.email,
  //       name: data.name,
  //       roles: data.roles,
  //     });
  //   },
  //   onSettled: () => {
  //     setAuthSuccess(false);
  //   },
  // });

  useEffect(() => {
    if (!isAuthSuccess) return;
    mutateGetCurrentUser();
  }, [isAuthSuccess]);

  useEffect(() => {
    if (storedUserLocal || storedUserSession) setUser(storedUserLocal || storedUserSession);
    if (user && !storedUserLocal && !storedUserSession) setStoredUserSession(user);
  }, []);

  const { mutate: mutateGetCurrentUser } = useMutation(api.getCurrentUser, {
    onSuccess: (data) => {
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        roles: data.roles,
      });
    },
  });
  const { mutate: mutateSignIn } = useMutation(api.signIn, {
    onSuccess: (res) => {
      console.log(res);
      setSignInSuccess(true);
      setSignOutSuccess(false);
      queryClient.invalidateQueries(['getIsAuthenticated']);
      // Save user data to local storage or session storage
      if (persist) {
        setStoredUserLocal(res);
      } else {
        setStoredUserSession(res);
      }
      setIsLoading(false);
      mutateGetCurrentUser();
    },
    onError: () => {
      setIsLoading(false);
      enqueueSnackbar(t('general.messages.error.login-invalid'), { variant: 'error' });
    },
  });
  const { mutate: mutateSignOut } = useMutation(api.signOut, {
    onSuccess: () => {
      setSignInSuccess(false);
      setSignOutSuccess(true);
      setUser(null);
      if (storedUserSession) sessionStorage.removeItem(STORAGE_KEYS.USER_KEY);
      if (storedUserLocal) localStorage.removeItem(STORAGE_KEYS.USER_KEY);
    },
  });

  const signIn = (id: string, password: string, persist: boolean) => {
    if (isSignInSuccess) return;
    setIsLoading(true);
    setPersist(persist);
    mutateSignIn({ id, password: encodeURIComponent(password), persist: persist });
    navigate(redirectPath === '' ? paths.HOME : redirectPath);
  };

  const signOut = () => {
    if (isSignOutSuccess) return;
    mutateSignOut();
  };

  /**
   * Redirect to previous page (before login page)
   */
  useEffect(() => {
    if (location.pathname === `/${paths.LOGIN_PAGE}`) return;
    setRedirectPath(location?.pathname);
  }, [location]);

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, isAuthSuccess, isLoading }} {...props} />
  );
}

export { AuthProvider, useAuth };
